// src/PortalHome/PortalHome.tsx
import { Box as Box14, Skeleton as Skeleton3, Typography as Typography10 } from "@mui/material";
import { PAGE_MAX_WIDTH, PAGE_PADDING as PAGE_PADDING2 } from "@hopdrive/sdk-theme";

// src/HomeShell/HomeShell.tsx
import { useId as useId2, useState } from "react";
import { Box as Box4 } from "@mui/material";
import { PAGE_PADDING } from "@hopdrive/sdk-theme";

// src/HomeShellAttentionStrip/HomeShellAttentionStrip.tsx
import { Box, Typography } from "@mui/material";
import { BiChevronDown, BiChevronRight, BiError, BiErrorCircle, BiInfoCircle } from "react-icons/bi";

// src/utils/attentionSeverity.ts
var SEVERITY_RANK = { error: 0, warning: 1, info: 2 };
function topSeverity(items) {
  if (items.length === 0) return null;
  return items.reduce((top, item) => SEVERITY_RANK[item.severity] < SEVERITY_RANK[top] ? item.severity : top, "info");
}

// src/HomeShellAttentionStrip/HomeShellAttentionStrip.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var SEVERITY_ICON = {
  error: /* @__PURE__ */ jsx(BiErrorCircle, {}),
  warning: /* @__PURE__ */ jsx(BiError, {}),
  info: /* @__PURE__ */ jsx(BiInfoCircle, {})
};
var HomeShellAttentionStrip = ({ items, expanded = false, onToggle }) => {
  const top = topSeverity(items);
  if (top === null) return null;
  const count = items.length;
  return /* @__PURE__ */ jsxs(Box, { sx: { border: 1, borderColor: "divider", borderLeft: 4, borderLeftColor: `${top}.main`, borderRadius: 1, bgcolor: "background.paper", overflow: "hidden" }, children: [
    /* @__PURE__ */ jsxs(
      Box,
      {
        role: "button",
        tabIndex: 0,
        onClick: onToggle,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") onToggle?.();
        },
        sx: { display: "flex", alignItems: "center", gap: 1.5, p: 1.5, cursor: onToggle ? "pointer" : "default", "&:hover": onToggle ? { bgcolor: "action.hover" } : void 0 },
        children: [
          /* @__PURE__ */ jsx(Box, { sx: { color: `${top}.main`, fontSize: 20, display: "flex" }, children: SEVERITY_ICON[top] }),
          /* @__PURE__ */ jsxs(Box, { sx: { flex: 1, minWidth: 0, overflow: "hidden" }, children: [
            /* @__PURE__ */ jsxs(Typography, { component: "span", sx: { fontWeight: 600 }, children: [
              count,
              " ",
              count === 1 ? "item needs" : "items need",
              " attention"
            ] }),
            !expanded && /* @__PURE__ */ jsxs(Typography, { component: "span", sx: { color: "text.secondary", ml: 1 }, children: [
              "\xB7 ",
              items[0].title
            ] })
          ] }),
          /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }, children: expanded ? "Hide" : "Show all" }),
          /* @__PURE__ */ jsx(Box, { sx: { color: "text.disabled", fontSize: 20, display: "flex", transform: expanded ? "rotate(180deg)" : "none", transition: "transform .15s ease" }, children: /* @__PURE__ */ jsx(BiChevronDown, {}) })
        ]
      }
    ),
    expanded && /* @__PURE__ */ jsx(Box, { sx: { borderTop: 1, borderColor: "divider" }, children: items.map((item, i) => {
      const meta = [item.detail, item.rooftopLabel].filter(Boolean).join(" \xB7 ");
      return /* @__PURE__ */ jsxs(
        Box,
        {
          role: item.onOpen ? "button" : void 0,
          tabIndex: item.onOpen ? 0 : void 0,
          onClick: item.onOpen,
          onKeyDown: item.onOpen ? (e) => {
            if (e.key === "Enter" || e.key === " ") item.onOpen?.();
          } : void 0,
          sx: {
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            p: 1.5,
            cursor: item.onOpen ? "pointer" : "default",
            borderTop: i === 0 ? 0 : 1,
            borderColor: "divider",
            "&:hover": item.onOpen ? { bgcolor: "action.hover" } : void 0
          },
          children: [
            /* @__PURE__ */ jsx(Box, { sx: { color: `${item.severity}.main`, fontSize: 18, display: "flex", mt: "2px" }, children: SEVERITY_ICON[item.severity] }),
            /* @__PURE__ */ jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [
              /* @__PURE__ */ jsx(Typography, { variant: "body2", children: item.title }),
              meta && /* @__PURE__ */ jsx(Typography, { variant: "caption", sx: { color: "text.secondary" }, children: meta })
            ] }),
            item.onOpen && /* @__PURE__ */ jsx(Box, { sx: { color: "text.disabled", fontSize: 18, display: "flex" }, children: /* @__PURE__ */ jsx(BiChevronRight, {}) })
          ]
        },
        `${i}-${item.title}`
      );
    }) })
  ] });
};
var HomeShellAttentionStrip_default = HomeShellAttentionStrip;

// src/HomeShellHeader/HomeShellHeader.tsx
import { useId } from "react";
import { Box as Box2, Skeleton, Typography as Typography2 } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var ICON_BOX = 40;
var ICON_SIZE = 24;
var ICON_TINT_ALPHA = 0.12;
var TITLE_FONT_SIZE = 24;
var SUBTITLE_FONT_SIZE = 12;
var TITLE_INSET_PX = 2;
var SUBTITLE_INSET_PX = 2;
var TEXT_EDGE_EM = 0.1365;
var TEXT_OVERRUN_EM = 0.11;
var edgePx = (fontSize) => `-${(TEXT_EDGE_EM * fontSize).toFixed(2)}px`;
var ONE_LINE_SX = {
  minWidth: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1,
  py: `${TEXT_OVERRUN_EM}em`,
  my: `-${TEXT_OVERRUN_EM}em`
};
var HomeShellHeader = ({ identity, headerActions, pending = false }) => {
  const subtitleId = useId();
  if (pending) {
    return /* @__PURE__ */ jsxs2(Box2, { sx: { display: "flex", alignItems: "center", gap: 1.5, minHeight: ICON_BOX }, children: [
      /* @__PURE__ */ jsx2(Skeleton, { variant: "rounded", width: ICON_BOX, height: ICON_BOX }),
      /* @__PURE__ */ jsxs2(Box2, { sx: { display: "flex", flexDirection: "column", justifyContent: "space-between", height: ICON_BOX }, children: [
        /* @__PURE__ */ jsx2(Skeleton, { variant: "rounded", width: 200, height: TITLE_FONT_SIZE }),
        /* @__PURE__ */ jsx2(Skeleton, { variant: "rounded", width: 240, height: SUBTITLE_FONT_SIZE })
      ] })
    ] });
  }
  const { title, subtitle, icon, accentColor } = identity;
  return /* @__PURE__ */ jsxs2(Box2, { sx: { display: "flex", alignItems: "flex-start", gap: 1.5, flexWrap: "wrap" }, children: [
    icon && /* @__PURE__ */ jsx2(
      Box2,
      {
        "aria-hidden": true,
        sx: (theme) => {
          const accent = accentColor ?? theme.palette.text.primary;
          return {
            flex: "0 0 auto",
            width: ICON_BOX,
            height: ICON_BOX,
            borderRadius: 1,
            display: "grid",
            placeItems: "center",
            fontSize: ICON_SIZE,
            color: accent,
            bgcolor: alpha(accent, ICON_TINT_ALPHA)
          };
        },
        children: icon
      }
    ),
    /* @__PURE__ */ jsxs2(Box2, { sx: { flex: "1 1 auto", minWidth: 0, height: ICON_BOX, display: "flex", flexDirection: "column", justifyContent: "space-between" }, children: [
      /* @__PURE__ */ jsx2(Box2, { sx: { display: "flex", alignItems: "center", gap: 1, minWidth: 0, mt: edgePx(TITLE_FONT_SIZE), pt: `${TITLE_INSET_PX}px` }, children: /* @__PURE__ */ jsx2(Typography2, { component: "h1", "aria-describedby": subtitle ? subtitleId : void 0, sx: { ...ONE_LINE_SX, fontSize: TITLE_FONT_SIZE, fontWeight: 600 }, children: title }) }),
      subtitle && /* @__PURE__ */ jsx2(Box2, { sx: { minWidth: 0, mb: edgePx(SUBTITLE_FONT_SIZE), pb: `${SUBTITLE_INSET_PX}px` }, children: /* @__PURE__ */ jsx2(Typography2, { id: subtitleId, component: "p", sx: { ...ONE_LINE_SX, color: "text.secondary", fontSize: SUBTITLE_FONT_SIZE }, children: subtitle }) })
    ] }),
    headerActions && /* @__PURE__ */ jsx2(Box2, { sx: { display: "flex", alignItems: "center", gap: 1, flexShrink: 0, ml: "auto", alignSelf: "center" }, children: headerActions })
  ] });
};
var HomeShellHeader_default = HomeShellHeader;

// src/HomeShellTabs/HomeShellTabs.tsx
import { Box as Box3, Skeleton as Skeleton2, Tab, Tabs } from "@mui/material";

// src/utils/resolveTabStrip.ts
function resolveTabStrip(tabs, requestedKey) {
  const requested = tabs.find((tab) => tab.key === requestedKey);
  return {
    visible: tabs,
    renders: tabs.length > 1,
    activeKey: (requested ?? tabs[0])?.key
  };
}

// src/HomeShellTabs/HomeShellTabs.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var STRIP_HEIGHT = 40;
var HomeShellTabs = ({ tabs, activeKey, onTabChange, pending = false, idBase }) => {
  if (tabs.length <= 1) return null;
  if (pending) {
    return /* @__PURE__ */ jsx3(Box3, { sx: { display: "flex", alignItems: "center", gap: 3, px: 1, height: STRIP_HEIGHT }, children: [56, 72].map((w) => /* @__PURE__ */ jsx3(Skeleton2, { variant: "text", width: w, height: 24 }, w)) });
  }
  const strip = resolveTabStrip(tabs, activeKey);
  return /* @__PURE__ */ jsx3(Box3, { children: /* @__PURE__ */ jsx3(
    Tabs,
    {
      value: strip.activeKey ?? false,
      onChange: (_, key) => onTabChange?.(key),
      variant: "scrollable",
      scrollButtons: "auto",
      allowScrollButtonsMobile: true,
      sx: { minHeight: STRIP_HEIGHT },
      children: strip.visible.map((tab) => /* @__PURE__ */ jsx3(
        Tab,
        {
          value: tab.key,
          disabled: tab.disabled,
          id: idBase ? `${idBase}-tab-${tab.key}` : void 0,
          "aria-controls": idBase ? `${idBase}-panel-${tab.key}` : void 0,
          sx: { minHeight: STRIP_HEIGHT, py: 0.5, textTransform: "none", fontWeight: 500 },
          label: /* @__PURE__ */ jsxs3(Box3, { component: "span", sx: { display: "inline-flex", alignItems: "center", gap: 0.75 }, children: [
            tab.label,
            tab.badge != null && tab.badge > 0 && /* @__PURE__ */ jsx3(
              Box3,
              {
                component: "span",
                sx: {
                  px: 0.75,
                  minWidth: 18,
                  height: 18,
                  borderRadius: 9,
                  bgcolor: "action.selected",
                  color: "text.secondary",
                  fontSize: 11,
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center"
                },
                children: tab.badge
              }
            )
          ] })
        },
        tab.key
      ))
    }
  ) });
};
var HomeShellTabs_default = HomeShellTabs;

// src/HomeShell/HomeShell.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var FILL_SX = { flex: "1 1 auto", minHeight: 0 };
var FILL_CONTENT_SX = { ...FILL_SX, display: "flex", flexDirection: "column" };
var HomeShell = ({
  identity,
  headerActions,
  banner,
  aboveTabs,
  tabs,
  activeKey,
  onTabChange,
  attentionItems,
  pending = false,
  fill = false,
  variant = "contained",
  maxWidth,
  children
}) => {
  const [attentionOpen, setAttentionOpen] = useState(false);
  const baseId = useId2();
  const hasStrip = !pending && !!tabs && tabs.length > 1;
  const showsStrip = !!tabs && tabs.length > 1;
  const activeTab = tabs?.find((tab) => tab.key === activeKey);
  const linkTabpanel = hasStrip && activeTab != null;
  const fullscreen = variant === "fullscreen";
  return /* @__PURE__ */ jsxs4(Box4, { sx: { display: "flex", flexDirection: "column", ...fill ? FILL_SX : {} }, children: [
    /* @__PURE__ */ jsx4(
      Box4,
      {
        sx: fullscreen ? { flex: "0 0 auto", bgcolor: "background.paper", borderBottom: 1, borderColor: "divider" } : { flex: "0 0 auto", width: "100%", maxWidth, mx: "auto", px: PAGE_PADDING, pt: PAGE_PADDING },
        children: /* @__PURE__ */ jsxs4(
          Box4,
          {
            sx: {
              display: "flex",
              flexDirection: "column",
              gap: 1,
              pb: showsStrip ? 0 : 1.5,
              ...fullscreen ? { px: PAGE_PADDING, pt: PAGE_PADDING } : { borderBottom: 1, borderColor: "divider" }
            },
            children: [
              /* @__PURE__ */ jsx4(HomeShellHeader_default, { identity, headerActions, pending }),
              /* @__PURE__ */ jsx4(HomeShellAttentionStrip_default, { items: attentionItems ?? [], expanded: attentionOpen, onToggle: () => setAttentionOpen((open) => !open) }),
              banner,
              aboveTabs,
              tabs && /* @__PURE__ */ jsx4(HomeShellTabs_default, { tabs, activeKey, onTabChange, pending, idBase: baseId })
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx4(
      Box4,
      {
        role: hasStrip ? "tabpanel" : void 0,
        id: linkTabpanel ? `${baseId}-panel-${activeTab.key}` : void 0,
        "aria-labelledby": linkTabpanel ? `${baseId}-tab-${activeTab.key}` : void 0,
        tabIndex: hasStrip ? 0 : void 0,
        sx: { minWidth: 0, ...fill ? FILL_CONTENT_SX : {} },
        children
      }
    )
  ] });
};
var HomeShell_default = HomeShell;

// src/HomeZone/HomeZone.tsx
import { Box as Box13, Typography as Typography9 } from "@mui/material";

// src/HomeDiscover/HomeDiscover.tsx
import { Box as Box5, Button, Typography as Typography3 } from "@mui/material";
import { alpha as alpha2 } from "@mui/material/styles";
import { HeroPanel, HERO_PANEL_NARROW as NARROW, heroButtonSx, heroLedeSx, heroTitleSx } from "@hopdrive/sdk-ui";

// src/utils/productIcons.tsx
import { PiCarFill, PiHandWavingFill, PiPackageFill, PiWrenchFill } from "react-icons/pi";

// src/utils/iconBox.ts
var iconBoxSx = (size, bgcolor, color) => ({
  flex: "0 0 auto",
  width: size,
  height: size,
  borderRadius: 1,
  display: "grid",
  placeItems: "center",
  bgcolor,
  color,
  fontSize: Math.round(size * 0.6)
});

// src/utils/productIcons.tsx
var PRODUCT_ICONS = {
  moves: PiCarFill,
  "guest-rides": PiHandWavingFill,
  "mobile-service": PiWrenchFill,
  "parts-delivery": PiPackageFill
};
var productAccent = (theme) => theme.palette.utility.main;
var productIconBoxSx = (theme, size) => iconBoxSx(size, productAccent(theme), theme.palette.common.white);

// src/HomeDiscover/HomeDiscover.tsx
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var OFFER_ICON_BOX = 30;
var AVAIL_LABEL_SX = { mb: 1.5, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "hero.contrastSecondary" };
var OfferCard = ({ offer, linkComponent }) => {
  const Icon = PRODUCT_ICONS[offer.product];
  return /* @__PURE__ */ jsxs5(
    Box5,
    {
      component: linkComponent,
      to: offer.to,
      sx: (theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 2,
        minWidth: 0,
        borderRadius: 1,
        textDecoration: "none",
        color: "hero.contrastText",
        border: 1,
        borderColor: "hero.divider",
        bgcolor: alpha2(theme.palette.common.white, 0.06),
        transition: "background-color .15s ease",
        "&:hover": { bgcolor: alpha2(theme.palette.common.white, 0.11) },
        "&:hover .discover-offer-link": { textDecoration: "underline" },
        "&:focus-visible": { outline: 2, outlineColor: "secondary.light", outlineOffset: 2 }
      }),
      children: [
        /* @__PURE__ */ jsxs5(Box5, { sx: { display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }, children: [
          /* @__PURE__ */ jsx5(Box5, { "aria-hidden": true, sx: (theme) => productIconBoxSx(theme, OFFER_ICON_BOX), children: /* @__PURE__ */ jsx5(Icon, {}) }),
          /* @__PURE__ */ jsx5(Typography3, { component: "span", sx: { fontSize: 15, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: offer.label })
        ] }),
        /* @__PURE__ */ jsx5(Typography3, { component: "span", sx: { fontSize: 12.5, lineHeight: 1.45, color: "hero.contrastSecondary" }, children: offer.pitch }),
        /* @__PURE__ */ jsx5(Box5, { component: "span", className: "discover-offer-link", sx: { display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 12.5, fontWeight: 600 }, children: "See how it works \u2192" })
      ]
    }
  );
};
var ActionButton = ({ action, linkComponent }) => {
  const shared = {
    startIcon: action.icon,
    variant: action.primary ? "contained" : "outlined",
    color: "primary",
    sx: heroButtonSx(action.primary ? "primary" : "quiet")
  };
  return action.to ? /* @__PURE__ */ jsx5(Button, { ...shared, component: linkComponent, to: action.to, children: action.label }) : /* @__PURE__ */ jsx5(Button, { ...shared, onClick: action.onSelect, children: action.label });
};
var HomeDiscover = ({ zone, linkComponent }) => /* @__PURE__ */ jsxs5(HeroPanel, { "aria-label": zone.title, glow: (theme) => theme.palette.utility.main, children: [
  /* @__PURE__ */ jsxs5(Box5, { sx: { minWidth: 0 }, children: [
    /* @__PURE__ */ jsx5(Typography3, { component: "h2", sx: heroTitleSx, children: zone.title }),
    /* @__PURE__ */ jsx5(Typography3, { sx: heroLedeSx, children: zone.intro })
  ] }),
  zone.offers.length > 0 && /* @__PURE__ */ jsxs5(Box5, { children: [
    /* @__PURE__ */ jsx5(Typography3, { component: "h3", sx: AVAIL_LABEL_SX, children: "Discover our products" }),
    /* @__PURE__ */ jsx5(Box5, { sx: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1.5, [NARROW]: { gridTemplateColumns: "1fr" } }, children: zone.offers.map((offer) => /* @__PURE__ */ jsx5(OfferCard, { offer, linkComponent }, offer.id)) })
  ] }),
  zone.actions.length > 0 && /* @__PURE__ */ jsx5(Box5, { sx: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25 }, children: zone.actions.map((action) => /* @__PURE__ */ jsx5(ActionButton, { action, linkComponent }, action.key)) })
] });
var HomeDiscover_default = HomeDiscover;

// src/HomeHero/HomeHero.tsx
import { Box as Box7, ToggleButton, ToggleButtonGroup, Typography as Typography5 } from "@mui/material";
import { HeroPanel as HeroPanel2, HERO_PANEL_NARROW as NARROW2, heroTitleSx as heroTitleSx2 } from "@hopdrive/sdk-ui";

// src/utils/heroLayout.ts
var BUCKET_ORDER = ["upcoming", "active", "completed", "cancelled"];
var FALLBACK_LABELS = {
  upcoming: "Upcoming",
  active: "Active",
  completed: "Completed",
  cancelled: "Canceled"
};
var arrangeFigures = (figures) => BUCKET_ORDER.map((id) => figures.find((figure) => figure.id === id) ?? { id, label: FALLBACK_LABELS[id], value: 0 });
var resolveHeroBody = (zone) => {
  const showSwitch = zone.showSwitch && zone.products.length > 0;
  const body = showSwitch && zone.view === "split" ? "split" : "combined";
  return { body, showSwitch, showIdentity: zone.identity !== void 0 };
};
var summarizeFigures = (figures) => {
  const arranged = arrangeFigures(figures);
  const byId = Object.fromEntries(arranged.map((figure) => [figure.id, figure]));
  return {
    total: arranged.reduce((sum, figure) => sum + figure.value, 0),
    active: byId.active.value,
    cancelled: byId.cancelled.value,
    truncated: arranged.some((figure) => figure.truncated === true)
  };
};
var capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);
var heroTitle = (zone, scopeLabel) => {
  const unit = capitalize(zone.unitLabel);
  let text;
  if (zone.identity) {
    const label = zone.identity.label;
    text = label.toLowerCase().endsWith(zone.unitLabel.toLowerCase()) ? `Today's ${label}` : `Today's ${label} ${unit}`;
  } else {
    text = zone.view === "combined" ? `Today's Combined ${unit}` : `Today's ${unit}`;
  }
  return { text, ...scopeLabel ? { subtitle: scopeLabel } : {} };
};
var heroGlow = (zone) => zone.identity ? { kind: "product", product: zone.identity.product } : { kind: "primary" };
var heroAccessibleName = (zone, scopeLabel) => {
  const title = heroTitle(zone, scopeLabel);
  return title.subtitle ? `${title.text} \xB7 ${title.subtitle}` : title.text;
};

// src/HomeHero/HeroFigure.tsx
import { Box as Box6, Typography as Typography4 } from "@mui/material";

// src/utils/figureLinkLabel.ts
var formatFigureValue = (value) => value.toLocaleString("en-US");
var accessibleFigureValue = (figure) => figure.truncated ? `at least ${formatFigureValue(figure.value)}` : formatFigureValue(figure.value);
var figureLinkLabel = (figure, subject) => {
  const body = `${accessibleFigureValue(figure)} ${figure.label}`;
  return subject ? `${subject}: ${body}` : body;
};

// src/utils/figureMarker.ts
var orderStatusColor = (theme, bucket) => theme.palette.orderStatus[bucket];

// src/HomeHero/HeroFigure.tsx
import { Fragment, jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var NUMBER_SX = {
  hero: { fontSize: 40, fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.03em" },
  card: { fontSize: 36, fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.02em" },
  row: { fontSize: 24, fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.02em" }
};
var INK = {
  hero: { number: "hero.contrastText", label: "hero.contrastSecondary", plus: "hero.contrastSecondary" },
  card: { number: "text.primary", label: "grey.500", plus: "grey.500" }
};
var MARKER_SIZE = 8;
var FIGURE_LINK_SX = {
  color: "inherit",
  textDecoration: "none",
  borderRadius: 0.5,
  "&:hover": { textDecoration: "underline", textDecorationColor: "currentColor", textUnderlineOffset: "0.15em" },
  "&:focus-visible": { outline: 2, outlineColor: "secondary.light", outlineOffset: 2 }
};
var HeroFigure = ({ figure, scale = "hero", surface = "hero", align = "start", linkComponent, subject }) => {
  const ink = INK[surface];
  const linked = figure.to !== void 0 && linkComponent !== void 0;
  const frameSx = { minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75, alignItems: align === "center" ? "center" : "stretch", textAlign: align === "center" ? "center" : "left" };
  const body = /* @__PURE__ */ jsxs6(Fragment, { children: [
    /* @__PURE__ */ jsxs6(
      Typography4,
      {
        component: "p",
        "aria-label": linked ? void 0 : figureLinkLabel(figure),
        "aria-hidden": linked || void 0,
        sx: { ...NUMBER_SX[scale], color: ink.number, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
        children: [
          /* @__PURE__ */ jsx6("span", { "aria-hidden": true, children: formatFigureValue(figure.value) }),
          figure.truncated && /* @__PURE__ */ jsx6(Box6, { component: "span", "aria-hidden": true, sx: { fontSize: "0.6em", fontWeight: 600, verticalAlign: "top", color: ink.plus, ml: 0.25 }, children: "+" })
        ]
      }
    ),
    /* @__PURE__ */ jsxs6(Box6, { "aria-hidden": linked || void 0, sx: { display: "flex", alignItems: "flex-start", justifyContent: align === "center" ? "center" : "flex-start", gap: 1, minWidth: 0, maxWidth: "100%" }, children: [
      /* @__PURE__ */ jsx6(Box6, { "aria-hidden": true, sx: (theme) => ({ flex: "0 0 auto", mt: "4px", width: MARKER_SIZE, height: MARKER_SIZE, borderRadius: "50%", bgcolor: orderStatusColor(theme, figure.id) }) }),
      /* @__PURE__ */ jsx6(Typography4, { variant: "overline", component: "span", sx: { color: ink.label, lineHeight: 1.4, minWidth: 0 }, children: figure.label })
    ] })
  ] });
  if (linked) {
    return /* @__PURE__ */ jsx6(Box6, { component: linkComponent, to: figure.to, "aria-label": figureLinkLabel(figure, subject), sx: { ...frameSx, ...FIGURE_LINK_SX }, children: body });
  }
  return /* @__PURE__ */ jsx6(Box6, { sx: frameSx, children: body });
};
var HeroFigure_default = HeroFigure;

// src/HomeHero/HomeHero.tsx
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
var VIEW_LABELS = { combined: "Combined", split: "By product" };
var HERO_LINK_SX = {
  display: "inline-flex",
  alignItems: "center",
  gap: 1,
  minWidth: 0,
  color: "hero.contrastText",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
  "&:focus-visible": { outline: 2, outlineColor: "secondary.light", outlineOffset: 2, borderRadius: 0.5 }
};
var IDENTITY_ICON_BOX = 32;
var ROW_ICON_BOX = 28;
var SUBTITLE_SX = { fontSize: 13, fontWeight: 500, lineHeight: 1.2, color: "hero.contrastSecondary" };
var HeroTitleBlock = ({ text, subtitle }) => /* @__PURE__ */ jsxs7(Box7, { sx: { display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }, children: [
  /* @__PURE__ */ jsx7(Typography5, { component: "h2", sx: { ...heroTitleSx2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: text }),
  subtitle && /* @__PURE__ */ jsx7(Typography5, { component: "span", sx: SUBTITLE_SX, children: subtitle })
] });
var HeroFigures = ({ zone, linkComponent, subject }) => /* @__PURE__ */ jsx7(Box7, { sx: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 3, [NARROW2]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", rowGap: 2.5 } }, children: arrangeFigures(zone.figures).map((figure) => /* @__PURE__ */ jsx7(HeroFigure_default, { figure, align: "center", linkComponent, subject }, figure.id)) });
var HeroProductLine = ({ row, linkComponent }) => {
  const Icon = PRODUCT_ICONS[row.product];
  return /* @__PURE__ */ jsxs7(
    Box7,
    {
      component: "li",
      sx: {
        display: "grid",
        gridTemplateColumns: "minmax(180px, 1fr) repeat(4, minmax(0, 1fr))",
        // Numbers top-align so a label that wraps never shifts its neighbors' figures.
        alignItems: "start",
        gap: 3,
        py: 2.5,
        borderTop: 1,
        borderColor: "hero.divider",
        "&:first-of-type": { borderTop: 0, pt: 0 },
        "&:last-of-type": { pb: 0 },
        [NARROW2]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", rowGap: 2, "& > :first-child": { gridColumn: "1 / -1" } }
      },
      children: [
        /* @__PURE__ */ jsxs7(Box7, { component: linkComponent, to: row.to, sx: { ...HERO_LINK_SX, gap: 1.5, fontSize: 16, fontWeight: 600 }, children: [
          /* @__PURE__ */ jsx7(Box7, { "aria-hidden": true, sx: (theme) => productIconBoxSx(theme, ROW_ICON_BOX), children: /* @__PURE__ */ jsx7(Icon, {}) }),
          /* @__PURE__ */ jsx7(Box7, { component: "span", sx: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: row.label })
        ] }),
        arrangeFigures(row.figures).map((figure) => /* @__PURE__ */ jsx7(HeroFigure_default, { figure, scale: "row", align: "center", linkComponent, subject: row.label }, figure.id))
      ]
    }
  );
};
var HomeHero = ({ zone, scopeLabel, onViewChange, linkComponent }) => {
  const layout = resolveHeroBody(zone);
  const title = heroTitle(zone, scopeLabel);
  const Icon = zone.identity ? PRODUCT_ICONS[zone.identity.product] : null;
  return /* @__PURE__ */ jsxs7(
    HeroPanel2,
    {
      "aria-label": heroAccessibleName(zone, scopeLabel),
      glow: (theme) => heroGlow(zone).kind === "product" ? theme.palette.utility.light : theme.palette.primary.main,
      children: [
        /* @__PURE__ */ jsxs7(Box7, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", minWidth: 0 }, children: [
          layout.showIdentity && zone.identity && Icon ? /* @__PURE__ */ jsxs7(Box7, { component: linkComponent, to: zone.identity.to, sx: { ...HERO_LINK_SX, gap: 1.5, alignItems: "center" }, children: [
            /* @__PURE__ */ jsx7(Box7, { "aria-hidden": true, sx: (theme) => productIconBoxSx(theme, IDENTITY_ICON_BOX), children: /* @__PURE__ */ jsx7(Icon, {}) }),
            /* @__PURE__ */ jsx7(HeroTitleBlock, { ...title })
          ] }) : /* @__PURE__ */ jsx7(HeroTitleBlock, { ...title }),
          layout.showSwitch && /* @__PURE__ */ jsx7(
            ToggleButtonGroup,
            {
              exclusive: true,
              size: "small",
              value: zone.view,
              onChange: (_event, view) => {
                if (view) onViewChange(view);
              },
              "aria-label": "Figures",
              sx: {
                flex: "0 0 auto",
                p: "2px",
                gap: "2px",
                borderRadius: 1.5,
                // A transparent track, so the selected segment is the one solid thing in the switch.
                bgcolor: "transparent",
                border: 1,
                borderColor: "hero.divider",
                "& .MuiToggleButtonGroup-grouped": {
                  border: 0,
                  borderRadius: 1,
                  px: 1.5,
                  minHeight: 30,
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "none",
                  color: "hero.contrastSecondary",
                  "&:hover": { bgcolor: "hero.action.hover", color: "hero.contrastText" },
                  "&.Mui-selected": { bgcolor: "hero.contrastText", color: "hero.background", fontWeight: 700, "&:hover": { bgcolor: "hero.contrastText" } },
                  "&.Mui-focusVisible": { outline: 2, outlineColor: "secondary.light", outlineOffset: 1 },
                  "&:not(:first-of-type), &:first-of-type": { borderRadius: 1, ml: 0 },
                  [NARROW2]: { minHeight: 44, flex: 1 }
                },
                [NARROW2]: { width: "100%", display: "flex" }
              },
              children: Object.keys(VIEW_LABELS).map((view) => /* @__PURE__ */ jsx7(ToggleButton, { value: view, "aria-label": `${VIEW_LABELS[view]} figures`, children: VIEW_LABELS[view] }, view))
            }
          )
        ] }),
        layout.body === "split" ? /* @__PURE__ */ jsx7(Box7, { component: "ul", "aria-label": "By product", sx: { listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column" }, children: zone.products.map((row) => /* @__PURE__ */ jsx7(HeroProductLine, { row, linkComponent }, row.id)) }) : /* @__PURE__ */ jsx7(HeroFigures, { zone, linkComponent, subject: zone.identity?.label })
      ]
    }
  );
};
var HomeHero_default = HomeHero;

// src/ProductCards/ProductCards.tsx
import { Box as Box9 } from "@mui/material";

// src/ProductCards/ProductCard.tsx
import { Box as Box8, Card, Typography as Typography6 } from "@mui/material";
import { alpha as alpha3 } from "@mui/material/styles";
import { jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
var HOME_CARD_SX = {
  position: "relative",
  overflow: "hidden",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  p: 2,
  pt: 2.5,
  borderRadius: 2,
  bgcolor: "background.paper",
  color: "text.primary",
  textDecoration: "none",
  transition: "transform 200ms ease, box-shadow 200ms ease",
  "&:hover": { transform: "translateY(-2px)" },
  "&:focus-visible": { outline: 2, outlineColor: "secondary.main", outlineOffset: 2 }
};
var CARD_KPI_SX = { fontSize: 28, fontWeight: 700, lineHeight: 1, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
var CARD_NAME_SX = { fontSize: 16, fontWeight: 600, lineHeight: 1.2, minWidth: 0, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" };
var CARD_ICON_BOX = 32;
var cardGlow = (theme) => `radial-gradient(circle at 88% 10%, ${alpha3(theme.palette.utility.light, 0.34)} 0%, ${alpha3(theme.palette.utility.light, 0)} 48%)`;
var productCardSurfaceSx = (theme) => ({
  ...HOME_CARD_SX,
  bgcolor: "hero.background",
  color: "hero.contrastText",
  backgroundImage: cardGlow(theme),
  boxShadow: theme.shadows[2],
  "&:hover": { ...HOME_CARD_SX["&:hover"], boxShadow: theme.shadows[4] }
});
var MARKER_SIZE2 = 8;
var format = (value) => value.toLocaleString("en-US");
var CardStatusSegment = ({ bucket, text, color = "text.secondary" }) => /* @__PURE__ */ jsxs8(Box8, { component: "span", sx: { display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }, children: [
  /* @__PURE__ */ jsx8(Box8, { "aria-hidden": true, sx: (theme) => ({ flex: "0 0 auto", width: MARKER_SIZE2, height: MARKER_SIZE2, borderRadius: "50%", bgcolor: orderStatusColor(theme, bucket) }) }),
  /* @__PURE__ */ jsx8(Typography6, { component: "span", sx: { fontSize: 13, fontWeight: 600, color, lineHeight: 1.4, whiteSpace: "nowrap" }, children: text })
] });
var ProductCard = ({ product, label, to, figures, stateHint, linkComponent }) => {
  const Icon = PRODUCT_ICONS[product];
  const summary = summarizeFigures(figures);
  const totalText = summary.truncated ? `at least ${format(summary.total)}` : format(summary.total);
  const cancelledText = summary.cancelled > 0 ? `${format(summary.cancelled)} canceled` : null;
  return /* @__PURE__ */ jsxs8(
    Card,
    {
      component: linkComponent,
      to,
      "aria-label": `${label}: ${totalText} today, ${format(summary.active)} active${cancelledText ? `, ${cancelledText}` : ""}${stateHint ? `, ${stateHint}` : ""}`,
      sx: productCardSurfaceSx,
      children: [
        /* @__PURE__ */ jsxs8(Box8, { "aria-hidden": true, sx: { display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0 }, children: [
          /* @__PURE__ */ jsxs8(Box8, { sx: { display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: "1 1 auto", minHeight: CARD_ICON_BOX }, children: [
            /* @__PURE__ */ jsx8(Box8, { sx: (theme) => productIconBoxSx(theme, CARD_ICON_BOX), children: /* @__PURE__ */ jsx8(Icon, {}) }),
            /* @__PURE__ */ jsx8(Typography6, { component: "h3", sx: CARD_NAME_SX, children: label })
          ] }),
          /* @__PURE__ */ jsxs8(Box8, { sx: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, flex: "0 0 auto" }, children: [
            /* @__PURE__ */ jsxs8(Typography6, { component: "p", sx: CARD_KPI_SX, children: [
              format(summary.total),
              summary.truncated && /* @__PURE__ */ jsx8(Box8, { component: "span", sx: { fontSize: "0.6em", fontWeight: 600, verticalAlign: "top", color: "hero.contrastSecondary", ml: 0.25 }, children: "+" })
            ] }),
            /* @__PURE__ */ jsx8(Typography6, { variant: "overline", component: "span", sx: { color: "hero.contrastSecondary", lineHeight: 1.4, whiteSpace: "nowrap" }, children: "Today" })
          ] })
        ] }),
        /* @__PURE__ */ jsxs8(Box8, { "aria-hidden": true, sx: { display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.5, minWidth: 0 }, children: [
          /* @__PURE__ */ jsx8(CardStatusSegment, { bucket: "active", text: `${format(summary.active)} active`, color: "hero.contrastSecondary" }),
          cancelledText && /* @__PURE__ */ jsx8(CardStatusSegment, { bucket: "cancelled", text: cancelledText, color: "hero.contrastSecondary" })
        ] }),
        stateHint && /* @__PURE__ */ jsx8(Typography6, { sx: { fontSize: 13, fontWeight: 500, color: "hero.contrastSecondary" }, children: stateHint })
      ]
    }
  );
};
var ProductCard_default = ProductCard;

// src/ProductCards/ProductCards.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var NARROW3 = "@container (max-width: 639px)";
var ProductCards = ({ zone, linkComponent }) => {
  if (zone.cards.length === 0) return null;
  return /* @__PURE__ */ jsx9(Box9, { role: "list", sx: { containerType: "inline-size", display: "flex", flexWrap: "wrap", alignItems: "stretch", gap: 2.5 }, children: zone.cards.map((card) => /* @__PURE__ */ jsx9(Box9, { role: "listitem", sx: { flex: "1 1 220px", maxWidth: 300, minWidth: 0, display: "flex", [NARROW3]: { flexBasis: "100%", maxWidth: "none" } }, children: /* @__PURE__ */ jsx9(ProductCard_default, { ...card, linkComponent }) }, card.id)) });
};
var ProductCards_default = ProductCards;

// src/RooftopBreakdown/RooftopBreakdown.tsx
import { Box as Box11 } from "@mui/material";

// src/RooftopBreakdown/RooftopSummary.tsx
import { ButtonBase, Box as Box10, Card as Card2, Typography as Typography7 } from "@mui/material";
import { alpha as alpha4 } from "@mui/material/styles";
import { PiStorefront } from "react-icons/pi";
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var format2 = (value) => value.toLocaleString("en-US");
var ICON_TINT_ALPHA2 = 0.12;
var rooftopIconBoxSx = (theme) => iconBoxSx(CARD_ICON_BOX, alpha4(theme.palette.text.primary, ICON_TINT_ALPHA2), theme.palette.text.primary);
var RooftopSummary = ({ id, label, orders, active, cancelled, onSelect }) => {
  const cancelledText = cancelled > 0 ? `${format2(cancelled)} canceled` : null;
  return /* @__PURE__ */ jsxs9(
    Card2,
    {
      component: ButtonBase,
      onClick: () => onSelect(id),
      "aria-label": `${label}: ${format2(orders)} orders today, ${format2(active)} active${cancelledText ? `, ${cancelledText}` : ""}. Show only this rooftop`,
      sx: (theme) => ({
        ...HOME_CARD_SX,
        pt: 2,
        alignItems: "stretch",
        textAlign: "left",
        boxShadow: theme.shadows[2],
        "&:hover": { ...HOME_CARD_SX["&:hover"], boxShadow: theme.shadows[4] }
      }),
      children: [
        /* @__PURE__ */ jsxs9(Box10, { "aria-hidden": true, sx: { display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0 }, children: [
          /* @__PURE__ */ jsxs9(Box10, { sx: { display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: "1 1 auto", minHeight: CARD_ICON_BOX }, children: [
            /* @__PURE__ */ jsx10(Box10, { sx: rooftopIconBoxSx, children: /* @__PURE__ */ jsx10(PiStorefront, {}) }),
            /* @__PURE__ */ jsx10(Typography7, { component: "h3", sx: CARD_NAME_SX, children: label })
          ] }),
          /* @__PURE__ */ jsxs9(Box10, { sx: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, flex: "0 0 auto" }, children: [
            /* @__PURE__ */ jsx10(Typography7, { component: "p", sx: CARD_KPI_SX, children: format2(orders) }),
            /* @__PURE__ */ jsx10(Typography7, { variant: "overline", component: "span", sx: { color: "grey.500", lineHeight: 1.4, whiteSpace: "nowrap" }, children: "Today" })
          ] })
        ] }),
        /* @__PURE__ */ jsxs9(Box10, { "aria-hidden": true, sx: { display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.5, minWidth: 0 }, children: [
          /* @__PURE__ */ jsx10(CardStatusSegment, { bucket: "active", text: `${format2(active)} active` }),
          cancelledText && /* @__PURE__ */ jsx10(CardStatusSegment, { bucket: "cancelled", text: cancelledText })
        ] })
      ]
    }
  );
};
var RooftopSummary_default = RooftopSummary;

// src/RooftopBreakdown/RooftopBreakdown.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
var NARROW4 = "@container (max-width: 639px)";
var RooftopBreakdown = ({ rooftops }) => {
  if (rooftops.length === 0) return null;
  return /* @__PURE__ */ jsx11(Box11, { role: "list", sx: { containerType: "inline-size", display: "flex", flexWrap: "wrap", alignItems: "stretch", gap: 2.5 }, children: rooftops.map((rooftop) => /* @__PURE__ */ jsx11(Box11, { role: "listitem", sx: { flex: "1 1 220px", maxWidth: 300, minWidth: 0, display: "flex", [NARROW4]: { flexBasis: "100%", maxWidth: "none" } }, children: /* @__PURE__ */ jsx11(RooftopSummary_default, { ...rooftop }) }, rooftop.id)) });
};
var RooftopBreakdown_default = RooftopBreakdown;

// src/HomeZone/HomeZonePlaceholder.tsx
import { Box as Box12, Typography as Typography8 } from "@mui/material";
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
var HomeZonePlaceholder = ({ title, description }) => /* @__PURE__ */ jsxs10(Box12, { sx: { p: 3, borderRadius: 1.5, border: 1, borderStyle: "dashed", borderColor: "divider", minWidth: 0 }, children: [
  /* @__PURE__ */ jsx12(Typography8, { component: "h2", sx: { fontSize: 16, fontWeight: 600, color: "text.primary" }, children: title }),
  /* @__PURE__ */ jsx12(Typography8, { sx: { fontSize: 13, color: "text.secondary", mt: 0.5 }, children: description })
] });
var HomeZonePlaceholder_default = HomeZonePlaceholder;

// src/HomeZone/HomeZone.tsx
import { Fragment as Fragment2, jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
var ZONE_TITLES = { product_cards: "Orders by Product", by_rooftop: "Orders by Rooftop" };
var ZoneHeading = ({ title, flush }) => /* @__PURE__ */ jsx13(Typography9, { component: "h2", sx: { mb: flush ? 0 : 1.5, fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary" }, children: title });
var HomeZone = ({ zone, scopeLabel, onHeroViewChange, linkComponent }) => {
  switch (zone.key) {
    case "hero":
      return /* @__PURE__ */ jsx13(HomeHero_default, { zone, scopeLabel, onViewChange: onHeroViewChange, linkComponent });
    case "discover":
      return /* @__PURE__ */ jsx13(HomeDiscover_default, { zone, linkComponent });
    case "product_cards":
      return /* @__PURE__ */ jsxs11(Box13, { component: "section", "aria-label": ZONE_TITLES.product_cards, children: [
        /* @__PURE__ */ jsx13(ZoneHeading, { title: ZONE_TITLES.product_cards }),
        /* @__PURE__ */ jsx13(ProductCards_default, { zone, linkComponent })
      ] });
    case "by_rooftop":
      return /* @__PURE__ */ jsxs11(Box13, { component: "section", "aria-label": ZONE_TITLES.by_rooftop, children: [
        /* @__PURE__ */ jsx13(ZoneHeading, { title: ZONE_TITLES.by_rooftop }),
        /* @__PURE__ */ jsx13(RooftopBreakdown_default, { rooftops: zone.rooftops })
      ] });
    default:
      return /* @__PURE__ */ jsx13(Box13, { component: "section", "aria-label": zone.placeholder.title, children: zone.content ? /* @__PURE__ */ jsxs11(Fragment2, { children: [
        /* @__PURE__ */ jsxs11(Box13, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }, children: [
          /* @__PURE__ */ jsx13(ZoneHeading, { title: zone.placeholder.title, flush: true }),
          zone.headerAction
        ] }),
        zone.content
      ] }) : /* @__PURE__ */ jsx13(HomeZonePlaceholder_default, { ...zone.placeholder }) });
  }
};
var HomeZone_default = HomeZone;

// src/PortalHome/PortalHome.tsx
import { jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
var EmptyState = ({ title, description }) => /* @__PURE__ */ jsxs12(Box14, { sx: { p: 4, borderRadius: 1.5, border: 1, borderStyle: "dashed", borderColor: "divider", textAlign: "center" }, children: [
  /* @__PURE__ */ jsx14(Typography10, { component: "h2", sx: { fontSize: 18, fontWeight: 600, color: "text.primary" }, children: title }),
  /* @__PURE__ */ jsx14(Typography10, { sx: { fontSize: 14, color: "text.secondary", mt: 1 }, children: description })
] });
var PendingZones = () => /* @__PURE__ */ jsxs12(Box14, { sx: { display: "flex", flexDirection: "column", gap: 4 }, children: [
  /* @__PURE__ */ jsx14(Skeleton3, { variant: "rounded", height: 172, sx: { borderRadius: 2 } }),
  /* @__PURE__ */ jsx14(Box14, { sx: { display: "flex", gap: 2.5, flexWrap: "wrap" }, children: [0, 1, 2].map((i) => /* @__PURE__ */ jsx14(Skeleton3, { variant: "rounded", height: 84, sx: { flex: "1 1 220px", borderRadius: 2 } }, i)) })
] });
var scopeLabelOf = (scope) => scope?.rooftopLabel;
var PortalHome = ({
  model,
  onTabChange,
  onHeroViewChange,
  linkComponent,
  headerActions,
  headerIcon,
  banner,
  attentionItems,
  maxWidth = PAGE_MAX_WIDTH,
  variant,
  fill,
  pending = false
}) => {
  const strip = resolveTabStrip(model.tabs, model.activeTab);
  const activeContent = model.content.find((tab) => tab.key === strip.activeKey);
  const scopeLabel = scopeLabelOf(model.scope);
  const zones = activeContent?.zones ?? [];
  const zoneProps = { scopeLabel, onHeroViewChange, linkComponent };
  return /* @__PURE__ */ jsx14(
    HomeShell_default,
    {
      identity: { ...model.identity, icon: headerIcon },
      headerActions,
      banner,
      attentionItems,
      tabs: model.tabs,
      activeKey: strip.activeKey,
      onTabChange: (key) => onTabChange(key),
      pending,
      maxWidth,
      variant,
      fill,
      children: /* @__PURE__ */ jsx14(Box14, { sx: { width: "100%", maxWidth, mx: "auto", px: PAGE_PADDING2, py: PAGE_PADDING2, minWidth: 0 }, children: pending ? /* @__PURE__ */ jsx14(PendingZones, {}) : model.empty ? /* @__PURE__ */ jsx14(EmptyState, { ...model.empty }) : /* @__PURE__ */ jsx14(Box14, { sx: { display: "flex", flexDirection: "column", gap: 4 }, children: zones.map((zone) => /* @__PURE__ */ jsx14(HomeZone_default, { zone, ...zoneProps }, zone.key)) }) })
    }
  );
};
var PortalHome_default = PortalHome;

// src/HomeShellBanner/HomeShellBanner.tsx
import { Alert, Box as Box15, Button as Button2 } from "@mui/material";
import { jsx as jsx15 } from "react/jsx-runtime";
var HomeShellBanner = ({ severity, message, actions = [] }) => /* @__PURE__ */ jsx15(
  Alert,
  {
    severity,
    sx: { alignItems: "center", border: 1, borderColor: `${severity}.main`, "& .MuiAlert-action": { alignItems: "center", py: 0 } },
    action: actions.length > 0 ? /* @__PURE__ */ jsx15(Box15, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: actions.map((action) => /* @__PURE__ */ jsx15(Button2, { color: severity, size: "small", onClick: action.onSelect, children: action.label }, action.key)) }) : void 0,
    children: message
  }
);
var HomeShellBanner_default = HomeShellBanner;
export {
  BUCKET_ORDER,
  HeroFigure_default as HeroFigure,
  HomeDiscover_default as HomeDiscover,
  HomeHero_default as HomeHero,
  HomeShell_default as HomeShell,
  HomeShellAttentionStrip_default as HomeShellAttentionStrip,
  HomeShellBanner_default as HomeShellBanner,
  HomeShellHeader_default as HomeShellHeader,
  HomeShellTabs_default as HomeShellTabs,
  HomeZone_default as HomeZone,
  HomeZonePlaceholder_default as HomeZonePlaceholder,
  PRODUCT_ICONS,
  PortalHome_default as PortalHome,
  ProductCard_default as ProductCard,
  ProductCards_default as ProductCards,
  RooftopBreakdown_default as RooftopBreakdown,
  RooftopSummary_default as RooftopSummary,
  accessibleFigureValue,
  arrangeFigures,
  figureLinkLabel,
  formatFigureValue,
  heroAccessibleName,
  heroGlow,
  heroTitle,
  iconBoxSx,
  orderStatusColor,
  productAccent,
  productIconBoxSx,
  resolveHeroBody,
  resolveTabStrip,
  scopeLabelOf,
  summarizeFigures,
  topSeverity
};
//# sourceMappingURL=index.mjs.map